import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Plus, Edit, Trash2, Search, Filter, ChevronDown, 
  Save, X, AlertCircle, CheckCircle, ArrowLeft, ArrowRight,
  PlusCircle, MinusCircle, CheckSquare, Square, Trash, RefreshCw,
  Image
} from 'lucide-react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { Routes, Route, useNavigate, Outlet } from 'react-router-dom';
import { supabase } from '@/src/supabase/client';
import { useAuth } from '@/src/context/AuthContext';
import Cropper, { Area } from 'react-easy-crop';

// Types
interface Card {
  id?: string;
  category_id: string | null;
  card_type: string | null;
  card_type_new?: string;
  name: string;
  description: string | null;
  packet: string | null;
  packet_new?: string;
  level: string | null;
  level_new?: string;
  approved: boolean | null;
  attributes: Record<string, string> | null;
  image_url: string | null; // Keep for backward compatibility
  images: CardImage[] | null; // New field for multiple images
}

// Add a new interface for card images
interface CardImage {
  id?: string;
  url: string;
  caption: string | null;
  is_primary: boolean;
  display_order: number;
}

// Interface for a single attribute
interface Attribute {
  key: string;
  value: string;
  isNewKey: boolean;
}

interface Category {
  id: string;
  name: string;
}

interface CardQueryParams {
  categoryId?: string;
  cardType?: string;
  searchQuery?: string;
  level?: string;
  page: number;
  limit: number;
}

// Custom hooks for data management
const useCategories = () => {
  return useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categories')
        .select('*');
      
      if (error) {
        throw error;
      }
      
      return data as Category[];
    }
  });
};

const useCards = (params: CardQueryParams) => {
  return useQuery({
    queryKey: ['cards', params],
    queryFn: async () => {
      let query = supabase
        .from('cards')
        .select('*, categories(name), images:card_images(*)', { count: 'exact' });
      
      if (params.categoryId) {
        query = query.eq('category_id', params.categoryId);
      }
      
      if (params.cardType) {
        query = query.eq('card_type', params.cardType);
      }
      
      if (params.level) {
        query = query.eq('level', params.level);
      }
      
      if (params.searchQuery) {
        query = query.ilike('name', `%${params.searchQuery}%`);
      }
      
      const { from, to } = getPagination(params.page, params.limit);
      query = query.range(from, to);
      
      const { data, error, count } = await query;
      
      if (error) {
        throw error;
      }
      
      return { 
        cards: data as (Card & { 
          categories: { name: string },
          images: CardImage[]
        })[], 
        totalCount: count || 0 
      };
    }
  });
};

// Custom hook to fetch all existing attribute keys
const useAttributeKeys = () => {
  return useQuery({
    queryKey: ['attributeKeys'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cards')
        .select('attributes');
      
      if (error) {
        throw error;
      }
      
      // Extract all unique attribute keys
      const keys = new Set<string>();
      data.forEach(item => {
        if (item.attributes) {
          Object.keys(item.attributes).forEach(key => keys.add(key));
        }
      });
      
      return Array.from(keys);
    }
  });
};

// Utility function for pagination
const getPagination = (page: number, limit: number) => {
  const from = (page - 1) * limit;
  const to = from + limit - 1;
  return { from, to };
};

// Utility function to generate a unique filename for uploaded images
const generateUniqueFileName = (originalName: string) => {
  const timestamp = new Date().getTime();
  const random = Math.floor(Math.random() * 1000);
  const extension = originalName.split('.').pop();
  return `${timestamp}_${random}.${extension}`;
};

// Utility function to create a blob from a data URL
const dataURLToBlob = (dataURL: string): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', dataURL, true);
    xhr.responseType = 'blob';
    xhr.onload = () => {
      if (xhr.status === 200) {
        resolve(xhr.response);
      } else {
        reject(new Error('Error creating blob from data URL'));
      }
    };
    xhr.onerror = () => reject(new Error('Network error'));
    xhr.send();
  });
};

// Function to get cropped image from canvas
const getCroppedImg = (
  imageSrc: string,
  pixelCrop: Area,
  rotation = 0
): Promise<string> => {
  const image = document.createElement('img');
  image.src = imageSrc;
  
  return new Promise((resolve, reject) => {
    image.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }
      
      // Set canvas dimensions to the cropped size
      canvas.width = pixelCrop.width;
      canvas.height = pixelCrop.height;
      
      // Draw the cropped image onto the canvas
      ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height
      );
      
      // Convert the canvas to a data URL and resolve
      resolve(canvas.toDataURL('image/jpeg', 0.9));
    };
    
    image.onerror = () => {
      reject(new Error('Error loading image'));
    };
  });
};

// Shared layout component
export const CardsAdminLayout: React.FC = () => {
  return (
    <div className="min-h-screen bg-linear-to-br from-blue-50 to-indigo-50">
      {/* Header section */}
      <div className="py-12 px-4 sm:px-6 lg:px-8 bg-linear-to-r from-indigo-600 to-purple-600">
        <div className="max-w-7xl mx-auto">
          <div className="md:flex md:items-center md:justify-between">
            <div className="md:w-2/3">
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">Cards Administration</h1>
              <p className="text-xl text-indigo-100 mb-6">
                Manage your cards library - add, edit, and remove cards from your collection.
              </p>
            </div>
            
            <div className="md:w-1/3 mt-8 md:mt-0">
              <div className="bg-white/10 rounded-xl p-4 border border-indigo-300/30">
                <div className="flex items-center mb-3">
                  <AlertCircle className="h-5 w-5 text-indigo-100 mr-2" />
                  <h3 className="text-white font-medium">Quick Stats</h3>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-indigo-100 text-sm">Total Cards</p>
                    <p className="text-white font-bold text-2xl" id="total-cards-count">-</p>
                  </div>
                  <div>
                    <p className="text-indigo-100 text-sm">Categories</p>
                    <p className="text-white font-bold text-2xl" id="total-categories-count">-</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <CardsAdminMainPage />
    </div>
  );
};

// Cards Admin Main Page
const CardsAdminMainPage: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  
  // State for filters and pagination
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showFilterDropdown, setShowFilterDropdown] = useState<boolean>(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [selectedCardType, setSelectedCardType] = useState<string>('');
  const [selectedLevel, setSelectedLevel] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(10);
  
  // State for modals
  const [showAddCardModal, setShowAddCardModal] = useState<boolean>(false);
  const [showEditCardModal, setShowEditCardModal] = useState<boolean>(false);
  const [showDeleteCardModal, setShowDeleteCardModal] = useState<boolean>(false);
  const [selectedCard, setSelectedCard] = useState<Card | null>(null);
  
  // State for notifications
  const [notification, setNotification] = useState<{
    show: boolean;
    message: string;
    type: 'success' | 'error';
  }>({
    show: false,
    message: '',
    type: 'success'
  });
  
  // Query params
  const cardQueryParams: CardQueryParams = {
    categoryId: selectedCategoryId || undefined,
    cardType: selectedCardType || undefined,
    searchQuery: searchQuery || undefined,
    level: selectedLevel || undefined,
    page: currentPage,
    limit: itemsPerPage
  };
  
  // Query hooks
  const { data: categoriesData, isLoading: isLoadingCategories } = useCategories();
  const { 
    data: cardsData, 
    isLoading: isLoadingCards,
    isError: isErrorCards
  } = useCards(cardQueryParams);
  const { data: attributeKeysData } = useAttributeKeys();
  
  // Update stats once data is loaded
  useEffect(() => {
    const totalCardsElem = document.getElementById('total-cards-count');
    const totalCategoriesElem = document.getElementById('total-categories-count');
    
    if (totalCardsElem && cardsData) {
      totalCardsElem.textContent = cardsData.totalCount.toString();
    }
    
    if (totalCategoriesElem && categoriesData) {
      totalCategoriesElem.textContent = categoriesData.length.toString();
    }
  }, [cardsData, categoriesData]);
  
  // Get unique card types from the data
  const uniqueCardTypes = Array.from(
    new Set(cardsData?.cards.map(card => `${card.card_type}`).filter(Boolean))
  );
  
  // Get unique levels from the data
  const uniqueLevels = Array.from(
    new Set(cardsData?.cards.map(card => `${card.level}`).filter(Boolean))
  );
  
  const mapCard = (c: Card) => {
    const { id, packet_new, card_type_new, level_new, ...card } = c;

    return {
      ...card,
      ...(!!id ? { id } : {}),
      packet: c.packet === '__new__' ? packet_new : c.packet,
      category_id: !!c.category_id ? c.category_id : undefined,
      card_type: c.card_type === '__new__' ? card_type_new : c.card_type,
      level: c.level === '__new__' ? level_new : c.level,
      approved: c.approved === null ? false : c.approved, // Default to false if null
      attributes: c.attributes || {}
    };
  }

  // Function to delete an image from storage
  const deleteImageFromStorage = async (imageUrl: string) => {
    try {
      const path = imageUrl.split('/').pop();
      if (!path) return;
      
      const { error } = await supabase.storage
        .from('media')
        .remove([path]);
      
      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error deleting image from storage:', error);
      throw error;
    }
  };

  // Mutation for creating cards
  const createCardMutation = useMutation({
    mutationFn: async (newCard: Omit<Card, 'id'>) => {
      // Prepare the card data without images for initial insert
      const { images, ...cardData } = mapCard(newCard);
      
      // Insert the card
      const { data, error } = await supabase
        .from('cards')
        .insert([cardData])
        .select();
      
      if (error) throw error;
      
      const insertedCard = data[0] as Card;
      
      // If there are images, insert them into the card_images table
      if (images && images.length > 0 && insertedCard.id) {
        const imagesWithCardId = images.map(image => ({
          card_id: insertedCard.id,
          url: image.url,
          caption: image.caption,
          is_primary: image.is_primary,
          display_order: image.display_order
        }));
        
        const { error: imageError } = await supabase
          .from('card_images')
          .insert(imagesWithCardId);

        if (imageError) throw imageError;
        
        // Update the image_url field with the primary image for backward compatibility
        const primaryImage = images.find(img => img.is_primary);
        if (primaryImage) {
          const { error: updateError } = await supabase
            .from('cards')
            .update({ image_url: primaryImage.url })
            .eq('id', insertedCard.id);
          
          if (updateError) throw updateError;
        }
      }
      
      return insertedCard;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cards'] });
      queryClient.invalidateQueries({ queryKey: ['attributeKeys'] });
      setShowAddCardModal(false);
      showNotification('Card added successfully!', 'success');
    },
    onError: (error) => {
      showNotification(`Error adding card: ${error.message}`, 'error');
    }
  });

  // Mutation for updating cards
  const updateCardMutation = useMutation({
    mutationFn: async (updatedCard: Card) => {
      if (!updatedCard || !updatedCard.id) {
        return;
      }
  
      // Prepare the card data without images for update
      const { images, ...cardData } = mapCard(updatedCard);
      
      // Update the card
      const { data, error } = await supabase
        .from('cards')
        .update(cardData)
        .eq('id', updatedCard.id)
        .select();
      
      if (error) throw error;
      
      // Handle images
      if (images && updatedCard.id) {
        // Get existing images
        const { data: existingImages, error: fetchError } = await supabase
          .from('card_images')
          .select('*')
          .eq('card_id', updatedCard.id);
        
        if (fetchError) throw fetchError;
        
        // Delete removed images from storage and database
        const existingUrls = new Set(images.map(img => img.url));
        const imagesToDelete = existingImages?.filter(img => !existingUrls.has(img.url)) || [];
        
        for (const img of imagesToDelete) {
          await deleteImageFromStorage(img.url);
        }
        
        if (imagesToDelete.length > 0) {
          const { error: deleteError } = await supabase
            .from('card_images')
            .delete()
            .in('id', imagesToDelete.map(img => img.id));
          
          if (deleteError) throw deleteError;
        }
        
        // Upsert images
        const imagesForUpsert = images.map(image => ({
          ...(image && image.id ? {id: image.id} : {}),
          card_id: updatedCard.id,
          url: image.url,
          caption: image.caption,
          is_primary: image.is_primary,
          display_order: image.display_order
        }));
        
        const { error: upsertError } = await supabase
          .from('card_images')
          .upsert(imagesForUpsert, { onConflict: 'id' });
        
        console.log(
          `upsert!`, upsertError
        )
        if (upsertError) throw upsertError;
        
        // Update the image_url field with the primary image for backward compatibility
        const primaryImage = images.find(img => img.is_primary);
        if (primaryImage) {
          const { error: updateError } = await supabase
            .from('cards')
            .update({ image_url: primaryImage.url })
            .eq('id', updatedCard.id);
          
          if (updateError) throw updateError;
        }
      }
      
      return data[0] as Card;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cards'] });
      queryClient.invalidateQueries({ queryKey: ['attributeKeys'] });
      setShowEditCardModal(false);
      showNotification('Card updated successfully!', 'success');
    },
    onError: (error) => {
      showNotification(`Error updating card: ${error.message}`, 'error');
    }
  });
  
  
  // Mutation for deleting cards
  const deleteCardMutation = useMutation({
    mutationFn: async (cardId: string) => {
      // First, get all images for the card
      const { data: cardImages, error: imagesError } = await supabase
        .from('card_images')
        .select('url')
        .eq('card_id', cardId);
      
      if (imagesError) throw imagesError;
      
      // Delete each image from storage
      for (const image of (cardImages || [])) {
        await deleteImageFromStorage(image.url);
      }
      
      // Delete all images from the database
      const { error: deleteImagesError } = await supabase
        .from('card_images')
        .delete()
        .eq('card_id', cardId);
      
      if (deleteImagesError) throw deleteImagesError;
      
      // Then delete the card
      const { error } = await supabase
        .from('cards')
        .delete()
        .eq('id', cardId);
      
      if (error) throw error;
      return cardId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cards'] });
      setShowDeleteCardModal(false);
      showNotification('Card deleted successfully!', 'success');
    },
    onError: (error) => {
      showNotification(`Error deleting card: ${error.message}`, 'error');
    }
  });
  
  // Handle add card button click
  const handleAddCardClick = () => {
    setShowAddCardModal(true);
  };
  
  // Handle edit card button click
  const handleEditCardClick = (card: Card) => {
    setSelectedCard(card);
    setShowEditCardModal(true);
  };
  
  // Handle delete card button click
  const handleDeleteCardClick = (card: Card) => {
    setSelectedCard(card);
    setShowDeleteCardModal(true);
  };
  
  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };
  
  // Show notification
  const showNotification = (message: string, type: 'success' | 'error') => {
    setNotification({
      show: true,
      message,
      type
    });
    
    // Hide notification after 3 seconds
    setTimeout(() => {
      setNotification(prev => ({ ...prev, show: false }));
    }, 3000);
  };
  
  // Calculate total pages
  const totalPages = Math.ceil((cardsData?.totalCount || 0) / itemsPerPage);
  
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Action bar */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          {/* Search */}
          <div className="relative w-full md:w-64">
            <input
              type="text"
              placeholder="Search cards..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
            />
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
          </div>
          
          {/* Filter dropdown */}
          <div className="relative">
            <button
              className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              onClick={() => setShowFilterDropdown(!showFilterDropdown)}
            >
              <Filter className="h-5 w-5 text-gray-600" />
              <span>Filters</span>
              <ChevronDown className="h-4 w-4 text-gray-600" />
            </button>
            
            {showFilterDropdown && (
              <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg p-4 z-10">
                <h4 className="font-medium text-gray-700 mb-2">Filter by Category</h4>
                <select
                  className="w-full p-2 border border-gray-300 rounded-lg mb-3"
                  value={selectedCategoryId}
                  onChange={(e) => {
                    setSelectedCategoryId(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="">All Categories</option>
                  {categoriesData?.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
                
                <h4 className="font-medium text-gray-700 mb-2">Filter by Type</h4>
                <select
                  className="w-full p-2 border border-gray-300 rounded-lg mb-3"
                  value={selectedCardType}
                  onChange={(e) => {
                    setSelectedCardType(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="">All Types</option>
                  {uniqueCardTypes.map((type) => (
                    <option key={type} value={`${type}`}>
                      {type}
                    </option>
                  ))}
                </select>
                
                <h4 className="font-medium text-gray-700 mb-2">Filter by Level</h4>
                <select
                  className="w-full p-2 border border-gray-300 rounded-lg mb-3"
                  value={selectedLevel}
                  onChange={(e) => {
                    setSelectedLevel(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="">All Levels</option>
                  {uniqueLevels.map((level) => (
                    <option key={level} value={`${level}`}>
                      {level}
                    </option>
                  ))}
                </select>
                
                <button
                  className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                  onClick={() => setShowFilterDropdown(false)}
                >
                  Apply Filters
                </button>
              </div>
            )}
          </div>
          
          {/* Add card button */}
          <button
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg flex items-center justify-center hover:bg-indigo-700"
            onClick={handleAddCardClick}
          >
            <Plus className="h-5 w-5 mr-2" />
            Add New Card
          </button>
        </div>
      </div>
      
      {/* Cards list */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Image
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Level
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Packet
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Approved
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Attributes
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
            {isLoadingCards ? (
                <tr>
                  <td colSpan={9} className="px-6 py-4 text-center text-sm text-gray-500">
                    Loading cards...
                  </td>
                </tr>
              ) : isErrorCards ? (
                <tr>
                  <td colSpan={9} className="px-6 py-4 text-center text-sm text-red-500">
                    Error loading cards. Please try again.
                  </td>
                </tr>
              ) : cardsData?.cards.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-6 py-4 text-center text-sm text-gray-500">
                    No cards found. Try changing your filters or add a new card.
                  </td>
                </tr>
              ) : (
                cardsData?.cards.map((card) => (
                  <tr key={card.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <CardImageGallery 
                        images={card.images} 
                        name={card.name} 
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {card.name}
                      {card.images && card.images.length > 1 && (
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                          {card.images.length} images
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {card.categories?.name || 'Uncategorized'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {card.card_type || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {card.level || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {card.packet || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {card.approved ? (
                        <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                      ) : (
                        <Circle className="h-5 w-5 text-gray-300 mx-auto" />
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {card.attributes && Object.keys(card.attributes).length > 0 ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {Object.keys(card.attributes).length} attributes
                        </span>
                      ) : (
                        'None'
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        className="text-indigo-600 hover:text-indigo-900 mr-4"
                        onClick={() => handleEditCardClick(card)}
                      >
                        <Edit className="h-5 w-5" />
                      </button>
                      <button
                        className="text-red-600 hover:text-red-900"
                        onClick={() => handleDeleteCardClick(card)}
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                  currentPage === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                  currentPage === totalPages
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{Math.min((currentPage - 1) * itemsPerPage + 1, cardsData?.totalCount || 0)}</span> to{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * itemsPerPage, cardsData?.totalCount || 0)}
                  </span>{' '}
                  of <span className="font-medium">{cardsData?.totalCount || 0}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                      currentPage === 1
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    <span className="sr-only">Previous</span>
                    <ArrowLeft className="h-5 w-5" aria-hidden="true" />
                  </button>
                  
                  {/* Page numbers */}
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`relative inline-flex items-center px-4 py-2 border ${
                        page === currentPage
                          ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      } text-sm font-medium`}
                    >
                      {page}
                    </button>
                  ))}
                  
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                      currentPage === totalPages
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    <span className="sr-only">Next</span>
                    <ArrowRight className="h-5 w-5" aria-hidden="true" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Notification */}
      {notification.show && (
        <div
          className={`fixed bottom-4 right-4 px-6 py-3 rounded-lg shadow-lg ${
            notification.type === 'success' ? 'bg-green-500' : 'bg-red-500'
          } text-white flex items-center`}
        >
          {notification.type === 'success' ? (
            <CheckCircle className="h-5 w-5 mr-2" />
          ) : (
            <AlertCircle className="h-5 w-5 mr-2" />
          )}
          <span>{notification.message}</span>
        </div>
      )}
      
      {/* Add Card Modal */}
      {showAddCardModal && (
        <CardModal
          key="add-card"
          isOpen={showAddCardModal}
          onClose={() => setShowAddCardModal(false)}
          card={null}
          categories={categoriesData || []}
          cardTypes={uniqueCardTypes}
          levels={uniqueLevels}
          attributeKeys={attributeKeysData || []}
          onSave={(newCard) => createCardMutation.mutate(newCard)}
          isSaving={createCardMutation.isPending}
          user={user}
        />
      )}
      
      {/* Edit Card Modal */}
      {showEditCardModal && selectedCard && (
        <CardModal
          key="edit-card"
          isOpen={showEditCardModal}
          onClose={() => setShowEditCardModal(false)}
          card={selectedCard}
          categories={categoriesData || []}
          cardTypes={uniqueCardTypes}
          levels={uniqueLevels}
          attributeKeys={attributeKeysData || []}
          onSave={(updatedCard) => updateCardMutation.mutate(updatedCard as Card)}
          isSaving={updateCardMutation.isPending}
          user={user}
        />
      )}
      
      {/* Delete Card Modal */}
      {showDeleteCardModal && selectedCard && selectedCard.id && (
        <DeleteCardModal
          isOpen={showDeleteCardModal}
          onClose={() => setShowDeleteCardModal(false)}
          card={selectedCard}
          onDelete={() => deleteCardMutation.mutate(selectedCard.id ?? '')}
          isDeleting={deleteCardMutation.isPending}
        />
      )}
    </div>
  );
};

// Missing Circle component since it wasn't imported originally
const Circle: React.FC<{ className: string }> = ({ className }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <circle cx="12" cy="12" r="10"></circle>
    </svg>
  );
};

// ImageCropModal Component
interface ImageCropModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageUrl: string;
  onCropComplete: (croppedImage: Blob) => void;
  caption?: string;
  onCaptionChange?: (caption: string) => void;
}

const ImageCropModal: React.FC<ImageCropModalProps> = ({
  isOpen,
  onClose,
  imageUrl,
  onCropComplete,
  caption = '',
  onCaptionChange
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [localCaption, setLocalCaption] = useState(caption);
  
  // Update local caption when prop changes
  useEffect(() => {
    setLocalCaption(caption);
  }, [caption]);
  
  const onCropChange = (location: { x: number; y: number }) => {
    setCrop(location);
  };
  
  const onZoomChange = (zoomValue: number) => {
    setZoom(zoomValue);
  };
  
  const onCropCompleteHandler = (croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };
  
  const handleSaveCroppedImage = async () => {
    try {
      setIsProcessing(true);
      
      if (!croppedAreaPixels) {
        throw new Error('No crop area selected');
      }
      
      const croppedImageDataUrl = await getCroppedImg(imageUrl, croppedAreaPixels);
      const croppedImageBlob = await dataURLToBlob(croppedImageDataUrl);
      
      // Pass caption if available
      if (onCaptionChange) {
        onCaptionChange(localCaption);
      }
      
      onCropComplete(croppedImageBlob);
      onClose();
    } catch (error) {
      console.error('Error saving cropped image:', error);
    } finally {
      setIsProcessing(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 text-center">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>
        
        <div className="inline-block w-full max-w-3xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-medium text-gray-900">
              Crop Image (2:3 Aspect Ratio)
            </h3>
            <button
              type="button"
              className="text-gray-400 hover:text-gray-500"
              onClick={onClose}
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          
          <div className="relative h-96 mb-6 bg-gray-200 rounded-lg overflow-hidden">
            <Cropper
              image={imageUrl}
              crop={crop}
              zoom={zoom}
              aspect={2/3}
              onCropChange={onCropChange}
              onCropComplete={onCropCompleteHandler}
              onZoomChange={onZoomChange}
            />
          </div>
          
          <div className="mb-6">
            <label htmlFor="zoom" className="block text-sm font-medium text-gray-700 mb-1">
              Zoom: {zoom.toFixed(1)}x
            </label>
            <input
              type="range"
              id="zoom"
              min={1}
              max={3}
              step={0.1}
              value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>
          
          {/* Caption input field */}
          {onCaptionChange && (
            <div className="mb-6">
              <label htmlFor="caption" className="block text-sm font-medium text-gray-700 mb-1">
                Caption (optional)
              </label>
              <input
                type="text"
                id="caption"
                value={localCaption}
                onChange={(e) => setLocalCaption(e.target.value)}
                placeholder="Add a caption for this image..."
                className="w-full p-2 border border-gray-300 rounded-lg"
              />
            </div>
          )}
          
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSaveCroppedImage}
              disabled={isProcessing}
              className={`px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center ${
                isProcessing ? 'opacity-70 cursor-not-allowed' : ''
              }`}
            >
              {isProcessing ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </>
              ) : (
                <>
                  <CheckSquare className="h-5 w-5 mr-1" />
                  Apply Crop
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Card Modal Component
interface CardModalProps {
  isOpen: boolean;
  onClose: () => void;
  card: Card | null;
  categories: Category[];
  cardTypes: string[];
  levels: string[];
  attributeKeys: string[];
  onSave: (card: Omit<Card, 'id'> | Card) => void;
  isSaving: boolean;
  user: any;
}

// Modified CardModal Component with multiple image support
const CardModal: React.FC<CardModalProps> = ({
  isOpen,
  onClose,
  card,
  categories,
  cardTypes,
  levels,
  attributeKeys,
  onSave,
  isSaving,
  user
}) => {
  const [formData, setFormData] = useState<Omit<Card, 'id'> | Card>({
    id: card?.id || '',
    category_id: card?.category_id || null,
    card_type: card?.card_type || null,
    name: card?.name || '',
    description: card?.description || null,
    packet: card?.packet || null,
    level: card?.level || null,
    approved: card?.approved === undefined ? false : card?.approved,
    attributes: card?.attributes || {},
    image_url: card?.image_url || null,
    images: card?.images || []
  });
  
  // Convert attributes object to array for UI
  const [attributesList, setAttributesList] = useState<Attribute[]>(() => {
    if (!card?.attributes) return [];
    
    return Object.entries(card.attributes).map(([key, value]) => ({
      key,
      value: value || '',
      isNewKey: false
    }));
  });
  
  const [formErrors, setFormErrors] = useState<{
    name?: string;
  }>({});
  
  // State for image handling
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState<boolean>(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);
  const [showCropModal, setShowCropModal] = useState<boolean>(false);
  const [selectedImageCaption, setSelectedImageCaption] = useState<string>('');
  
  // Initialize images state
  const [images, setImages] = useState<CardImage[]>(() => {
    if (card?.images && card.images.length > 0) {
      return card.images;
    } else if (card?.image_url) {
      // Convert legacy single image to new format
      return [{
        url: card.image_url,
        caption: null,
        is_primary: true,
        display_order: 0
      }];
    } else {
      return [];
    }
  });
  
  // Update form data when images change
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      images,
      // Update image_url for backward compatibility
      image_url: images.find(img => img.is_primary)?.url || null
    }));
  }, [images]);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value === '' ? null : value
    }));
    
    // Clear error when field is changed
    if (name === 'name' && formErrors.name) {
      setFormErrors(prev => ({ ...prev, name: undefined }));
    }
  };
  
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };
  
  // Handle attribute changes
  const handleAttributeChange = (index: number, field: 'key' | 'value', value: string) => {
    const updatedAttributes = [...attributesList];
    updatedAttributes[index][field] = value;
    setAttributesList(updatedAttributes);
    
    // Update formData.attributes
    updateFormDataAttributes(updatedAttributes);
  };
  
  // Handle attribute key selection
  const handleAttributeKeySelect = (index: number, value: string) => {
    const updatedAttributes = [...attributesList];
    
    if (value === '__new__') {
      updatedAttributes[index].isNewKey = true;
      updatedAttributes[index].key = '';
    } else {
      updatedAttributes[index].isNewKey = false;
      updatedAttributes[index].key = value;
    }
    
    setAttributesList(updatedAttributes);
    updateFormDataAttributes(updatedAttributes);
  };
  
  // Add new attribute
  const addAttribute = () => {
    setAttributesList([
      ...attributesList,
      { key: '', value: '', isNewKey: true }
    ]);
  };
  
  // Remove attribute
  const removeAttribute = (index: number) => {
    const updatedAttributes = attributesList.filter((_, i) => i !== index);
    setAttributesList(updatedAttributes);
    updateFormDataAttributes(updatedAttributes);
  };
  
  // Update formData.attributes based on attributesList
  const updateFormDataAttributes = (attrs: Attribute[]) => {
    const attributesObj: Record<string, string> = {};
    
    attrs.forEach(attr => {
      if (attr.key && attr.key.trim() !== '') {
        attributesObj[attr.key] = attr.value;
      }
    });
    
    setFormData(prev => ({
      ...prev,
      attributes: attributesObj
    }));
  };
  
  // Image handling functions
  const handleImageSelect = (file: File) => {
    if (!file) return;
    
    setImageFile(file);
    
    // Create a preview URL
    const fileReader = new FileReader();
    fileReader.onload = () => {
      if (typeof fileReader.result === 'string') {
        setSelectedImageUrl(fileReader.result);
        setSelectedImageCaption('');
        setShowCropModal(true);
      }
    };
    fileReader.readAsDataURL(file);
  };
  
  // Modified handleCropComplete function in the CardModal component
  const handleCropComplete = async (croppedImageBlob: Blob) => {
    setShowCropModal(false);
    setIsUploadingImage(true);
    
    try {
      // Create a unique filename
      const originalName = imageFile?.name || 'image.jpg';
      const fileName = generateUniqueFileName(originalName);
      
      // Upload the cropped image to Supabase Storage
      const { data, error } = await supabase.storage
        .from('media')
        .upload(`public/cards/${fileName}`, croppedImageBlob);
      
      if (error) {
        throw error;
      }
      
      // Get the public URL
      const { data: urlData } = supabase.storage
        .from('media')
        .getPublicUrl(`public/cards/${fileName}`);
      
      // Create new image object
      const newImage: CardImage = {
        url: urlData.publicUrl,
        caption: selectedImageCaption || null,
        is_primary: images.length === 0, // Make primary if it's the first image
        display_order: images.length // Set display order to end of list
      };
      
      // If we have a card ID (editing existing card), immediately upsert to database
      if (formData.id) {
        const imageForUpsert = {
          card_id: formData.id,
          url: newImage.url,
          caption: newImage.caption,
          is_primary: newImage.is_primary,
          display_order: newImage.display_order
        };
        
        // Upsert the image to the database
        const { data: upsertData, error: upsertError } = await supabase
          .from('card_images')
          .upsert([imageForUpsert])
          .select();
        
        if (upsertError) {
          console.error('Error upserting image:', upsertError);
        } else if (upsertData && upsertData.length > 0) {
          // Update the image with the database ID
          newImage.id = upsertData[0].id;
          
          // If this is the primary image, also update the card's image_url field
          if (newImage.is_primary) {
            const { error: updateError } = await supabase
              .from('cards')
              .update({ image_url: newImage.url })
              .eq('id', formData.id);
            
            if (updateError) {
              console.error('Error updating card image_url:', updateError);
            }
          }
        }
      }
      
      // Add the new image to the local state
      setImages(prev => [...prev, newImage]);
    } catch (error) {
      console.error('Error uploading image:', error);
      // Show error notification
    } finally {
      setIsUploadingImage(false);
    }
  };
  
  const validateForm = (): boolean => {
    const errors: { name?: string } = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSave(formData);
    }
  };
  
  // Handle caption changes from crop modal
  const handleCaptionChange = (caption: string) => {
    setSelectedImageCaption(caption);
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 text-center">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>
        
        <div className="inline-block w-full max-w-2xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-medium text-gray-900">
              {card ? 'Edit Card' : 'Add New Card'}
            </h3>
            <button
              type="button"
              className="text-gray-400 hover:text-gray-500"
              onClick={onClose}
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="max-h-[70vh] overflow-y-auto pr-2">
            {/* Images Section */}
            <div className="mb-6">
              <MultiImageManager 
                images={images}
                onImagesChange={setImages}
                onUploadImage={handleImageSelect}
                isUploading={isUploadingImage}
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <select
                  name="category_id"
                  value={formData.category_id || ''}
                  onChange={e => handleChange(e)}
                  className="w-full p-2 border border-gray-300 rounded-lg"
                >
                  <option value="">None</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Card Type
                </label>
                <select
                  name="card_type"
                  value={formData.card_type || ''}
                  onChange={e => handleChange(e)}
                  className="w-full p-2 border border-gray-300 rounded-lg"
                >
                  <option value="">None</option>
                  {cardTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                  <option value="__new__">Add New Type...</option>
                </select>
                
                {formData.card_type === '__new__' && (
                  <input
                    type="text"
                    name="card_type_new"
                    placeholder="Enter new card type"
                    className="w-full p-2 border border-gray-300 rounded-lg mt-2"
                    onChange={e => handleChange(e)}
                  />
                )}
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={e => handleChange(e)}
                className={`w-full p-2 border ${
                  formErrors.name ? 'border-red-500' : 'border-gray-300'
                } rounded-lg`}
                required
              />
              {formErrors.name && (
                <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>
              )}
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description || ''}
                onChange={e => handleChange(e)}
                rows={4}
                className="w-full p-2 border border-gray-300 rounded-lg"
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Packet
                </label>
                <select
                  name="packet"
                  value={formData.packet || ''}
                  onChange={e => handleChange(e)}
                  className="w-full p-2 border border-gray-300 rounded-lg"
                >
                  <option value="">None</option>
                  {['LSAT'].map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                  <option value="__new__">Add New Packet...</option>
                </select>
                
                {formData.packet === '__new__' && (
                  <input
                    type="text"
                    name="packet_new"
                    placeholder="Enter new packet"
                    className="w-full p-2 border border-gray-300 rounded-lg mt-2"
                    onChange={e => handleChange(e)}
                  />
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Level
                </label>
                <select
                  name="level"
                  value={formData.level || ''}
                  onChange={e => handleChange(e)}
                  className="w-full p-2 border border-gray-300 rounded-lg"
                >
                  <option value="">None</option>
                  {levels.map((level) => (
                    <option key={level} value={level}>
                      {level}
                    </option>
                  ))}
                  <option value="__new__">Add New Level...</option>
                </select>
                
                {formData.level === '__new__' && (
                  <input
                    type="text"
                    name="level_new"
                    placeholder="Enter new level"
                    className="w-full p-2 border border-gray-300 rounded-lg mt-2"
                    onChange={e => handleChange(e)}
                  />
                )}
              </div>
            </div>
            
            {/* Approved Checkbox */}
            <div className="mb-6">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="approved"
                  name="approved"
                  checked={formData.approved === true}
                  onChange={handleCheckboxChange}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="approved" className="ml-2 block text-sm text-gray-700">
                  Approved
                </label>
              </div>
            </div>
            
            {/* Attributes Section */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <h4 className="text-sm font-medium text-gray-700">Attributes</h4>
                <button
                  type="button"
                  onClick={addAttribute}
                  className="flex items-center text-sm text-indigo-600 hover:text-indigo-900"
                >
                  <PlusCircle className="h-4 w-4 mr-1" />
                  Add Attribute
                </button>
              </div>
              
              {attributesList.length === 0 ? (
                <p className="text-sm text-gray-500 italic">No attributes added yet.</p>
              ) : (
                <div className="space-y-3">
                  {attributesList.map((attr, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <div className="flex-1">
                        {attr.isNewKey ? (
                          <input
                            type="text"
                            placeholder="Attribute name"
                            value={attr.key}
                            onChange={(e) => handleAttributeChange(index, 'key', e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded-lg"
                          />
                        ) : (
                          <select
                            value={attr.key}
                            onChange={(e) => handleAttributeKeySelect(index, e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded-lg"
                          >
                            {attr.key === '' && <option value="">Select attribute</option>}
                            {attributeKeys.map((key) => (
                              <option key={key} value={key}>
                                {key}
                              </option>
                            ))}
                            <option value="__new__">Add New Attribute...</option>
                          </select>
                        )}
                      </div>
                      <div className="flex-1">
                        <input
                          type="text"
                          placeholder="Value"
                          value={attr.value}
                          onChange={(e) => handleAttributeChange(index, 'value', e.target.value)}
                          className="w-full p-2 border border-gray-300 rounded-lg"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeAttribute(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <MinusCircle className="h-5 w-5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSaving || isUploadingImage}
                className={`px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center ${
                  (isSaving || isUploadingImage) ? 'opacity-70 cursor-not-allowed' : ''
                }`}
              >
                {isSaving ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-5 w-5 mr-1" />
                    Save Card
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
      
      {/* Image Crop Modal */}
      {showCropModal && selectedImageUrl && (
        <ImageCropModal
          isOpen={showCropModal}
          onClose={() => setShowCropModal(false)}
          imageUrl={selectedImageUrl}
          onCropComplete={handleCropComplete}
          caption={selectedImageCaption}
          onCaptionChange={handleCaptionChange}
        />
      )}
    </div>
  );
};

// Delete Card Modal Component
interface DeleteCardModalProps {
  isOpen: boolean;
  onClose: () => void;
  card: Card;
  onDelete: () => void;
  isDeleting: boolean;
}

const DeleteCardModal: React.FC<DeleteCardModalProps> = ({
  isOpen,
  onClose,
  card,
  onDelete,
  isDeleting
}) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 text-center">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>
        
        <div className="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-medium text-gray-900">
              Confirm Delete
            </h3>
            <button
              type="button"
              className="text-gray-400 hover:text-gray-500"
              onClick={onClose}
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          
          <div className="mb-6">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            
            {card.image_url && (
              <div className="w-24 h-36 mx-auto mb-4 bg-gray-100 rounded-lg overflow-hidden">
                <img
                  src={card.image_url}
                  alt={card.name}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            
            <p className="text-center text-gray-700">
              Are you sure you want to delete this card?
              <br />
              <span className="font-medium">{card.name}</span>
            </p>
            <p className="text-center text-gray-500 text-sm mt-2">
              This action cannot be undone. The card and its image will be permanently deleted.
            </p>
          </div>
          
          <div className="flex justify-center space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onDelete}
              disabled={isDeleting}
              className={`px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center ${
                isDeleting ? 'opacity-70 cursor-not-allowed' : ''
              }`}
            >
              {isDeleting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-5 w-5 mr-1" />
                  Delete Card
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Multi-Image Gallery component for card list view
const CardImageGallery: React.FC<{ images: CardImage[] | null; name: string }> = ({ 
  images, 
  name 
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);
  
  if (!images || images.length === 0) {
    // Fallback if no images
    return (
      <div className="flex-shrink-0 h-16 w-10 bg-gray-100 rounded-md overflow-hidden flex items-center justify-center text-gray-400">
        <Image className="h-6 w-6" />
      </div>
    );
  }
  
  // Sort images by display order
  const sortedImages = [...images].sort((a, b) => a.display_order - b.display_order);
  const currentImage = sortedImages[currentImageIndex];
  
  return (
    <div className="relative flex-shrink-0 h-16 w-10 bg-gray-100 rounded-md overflow-hidden">
      <img 
        src={currentImage.url} 
        alt={currentImage.caption || name} 
        className="h-full w-full object-cover"
      />
      
      {/* Only show navigation if there are multiple images */}
      {sortedImages.length > 1 && (
        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 px-1 py-0.5 flex justify-between items-center">
          <button 
            onClick={(e) => {
              e.stopPropagation();
              setCurrentImageIndex(prev => (prev === 0 ? sortedImages.length - 1 : prev - 1));
            }}
            className="text-white opacity-80 hover:opacity-100"
            disabled={sortedImages.length <= 1}
          >
            <ArrowLeft className="h-3 w-3" />
          </button>
          
          <span className="text-white text-xs">
            {currentImageIndex + 1}/{sortedImages.length}
          </span>
          
          <button 
            onClick={(e) => {
              e.stopPropagation();
              setCurrentImageIndex(prev => (prev === sortedImages.length - 1 ? 0 : prev + 1));
            }}
            className="text-white opacity-80 hover:opacity-100"
            disabled={sortedImages.length <= 1}
          >
            <ArrowRight className="h-3 w-3" />
          </button>
        </div>
      )}
    </div>
  );
};

// Multi-Image Management component for card edit/add modal
interface MultiImageManagerProps {
  images: CardImage[];
  onImagesChange: (images: CardImage[]) => void;
  onUploadImage: (file: File) => Promise<void>;
  isUploading: boolean;
}

const MultiImageManager: React.FC<MultiImageManagerProps> = ({
  images,
  onImagesChange,
  onUploadImage,
  isUploading
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Sort images by display order
  const sortedImages = [...images].sort((a, b) => a.display_order - b.display_order);
  
  const handleMakePrimary = (index: number) => {
    const updatedImages = images.map((img, i) => ({
      ...img,
      is_primary: i === index
    }));
    onImagesChange(updatedImages);
  };
  
  const handleRemoveImage = (index: number) => {
    const updatedImages = images.filter((_, i) => i !== index);
    
    // Update display order
    const reorderedImages = updatedImages.map((img, i) => ({
      ...img,
      display_order: i
    }));
    
    // Ensure there's always a primary image if any images exist
    if (reorderedImages.length > 0 && !reorderedImages.some(img => img.is_primary)) {
      reorderedImages[0].is_primary = true;
    }
    
    onImagesChange(reorderedImages);
  };
  
  const handleMoveImage = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === sortedImages.length - 1)
    ) {
      return;
    }
    
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    // Swap display_order values
    const updatedImages = [...sortedImages];
    const temp = updatedImages[index].display_order;
    updatedImages[index].display_order = updatedImages[newIndex].display_order;
    updatedImages[newIndex].display_order = temp;
    
    onImagesChange(updatedImages);
  };
  
  const handleCaptionChange = (index: number, caption: string) => {
    const updatedImages = sortedImages.map((img, i) => 
      i === index ? { ...img, caption } : img
    );
    onImagesChange(updatedImages);
  };
  
  const handleFileSelect = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      await onUploadImage(file);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h4 className="text-sm font-medium text-gray-700">
          Card Images ({sortedImages.length})
        </h4>
        <button
          type="button"
          onClick={handleFileSelect}
          disabled={isUploading}
          className={`flex items-center text-sm ${
            isUploading 
              ? 'text-gray-400 cursor-not-allowed' 
              : 'text-indigo-600 hover:text-indigo-900'
          }`}
        >
          {isUploading ? (
            <>
              <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
              Uploading...
            </>
          ) : (
            <>
              <PlusCircle className="h-4 w-4 mr-1" />
              Add Image
            </>
          )}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileChange}
        />
      </div>
      
      {sortedImages.length === 0 ? (
        <div className="text-center py-6 bg-gray-50 border border-dashed border-gray-300 rounded-lg">
          <Image className="h-12 w-12 mx-auto text-gray-400 mb-2" />
          <p className="text-sm text-gray-500">No images yet. Click "Add Image" to upload.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {sortedImages.map((image, index) => (
            <div 
              key={image.url} 
              className="flex bg-gray-50 border border-gray-200 rounded-lg overflow-hidden"
            >
              {/* Image preview */}
              <div className="w-20 h-30 flex-shrink-0">
                <img 
                  src={image.url} 
                  alt={image.caption || `Image ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* Image details */}
              <div className="flex-1 p-3">
                <div className="flex justify-between mb-2">
                  <div className="flex items-center">
                    <button
                      type="button"
                      onClick={() => handleMakePrimary(index)}
                      className={`mr-2 ${
                        image.is_primary 
                          ? 'text-indigo-600' 
                          : 'text-gray-400 hover:text-gray-600'
                      }`}
                      title={image.is_primary ? 'Primary Image' : 'Make Primary'}
                    >
                      {image.is_primary ? (
                        <CheckCircle className="h-5 w-5" />
                      ) : (
                        <Circle className="h-5 w-5" />
                      )}
                    </button>
                    <span className="text-sm font-medium">
                      {image.is_primary ? 'Primary Image' : `Image ${index + 1}`}
                    </span>
                  </div>
                  
                  <div className="flex space-x-1">
                    <button
                      type="button"
                      onClick={() => handleMoveImage(index, 'up')}
                      disabled={index === 0}
                      className={index === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:text-gray-700'}
                      title="Move Up"
                    >
                      <ArrowUp className="h-5 w-5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleMoveImage(index, 'down')}
                      disabled={index === sortedImages.length - 1}
                      className={index === sortedImages.length - 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:text-gray-700'}
                      title="Move Down"
                    >
                      <ArrowDown className="h-5 w-5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(index)}
                      className="text-red-500 hover:text-red-700"
                      title="Remove Image"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
                
                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    Caption (optional)
                  </label>
                  <input
                    type="text"
                    value={image.caption || ''}
                    onChange={(e) => handleCaptionChange(index, e.target.value)}
                    placeholder="Add a caption..."
                    className="w-full p-1 text-sm border border-gray-300 rounded"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Import missing icons
import { ArrowUp, ArrowDown } from 'lucide-react';
